import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, ContentSection, SEOContent, CTASection } from '../components/shared';
import { pageHeroImages } from '../data/mock';
import { ArrowRight, ArrowDown } from 'lucide-react';

const spezialFeatures = [
  {
    id: 'sport',
    title: 'Sporttauglichkeitsuntersuchung',
    description: 'Medizinische Freigabe für sportliche Aktivitäten.',
    image: '/images/Content_Behandlung_4.webp'
  },
  {
    id: 'fuehrerschein',
    title: 'Führerschein- & Eignungsuntersuchungen',
    description: 'Ärztliche Untersuchung für Führerschein und Beruf.',
    image: '/images/Sehtest_Fuehrerschein.webp'
  },
  {
    id: 'reise',
    title: 'Reisemedizin & Atteste',
    description: 'Beratung und Bescheinigungen für Ihre Reise.',
    image: '/images/Content_Behandlung_6.webp'
  },
  {
    id: 'checkup',
    title: 'Individuelle Check-up-Programme',
    description: 'Maßgeschneiderte Vorsorge für Ihre Bedürfnisse.',
    image: '/images/Checkup35_neu.webp'
  },
  {
    id: 'aesthetik',
    title: 'Ästhetische Behandlungen',
    description: 'Ausgewählte ästhetische Medizin für Ihr Wohlbefinden.',
    image: '/images/Botox.webp'
  }
];

export const SpezialPage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      {/* 2G.1 HERO */}
      <PageHero
        title="Spezial-Checks & Individuelle Leistungen im Medicum Diez"
        subtitle="Individuelle Gesundheitsleistungen, Vorsorgeprogramme und ausgewählte ästhetische Behandlungen."
        backgroundImage={pageHeroImages.spezial}
        ctaText="Beratung buchen"
      />

      {/* Schnellnavigation - Anchor Buttons */}
      <section className="py-6 bg-[#032925]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('leistungen')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Unsere Leistungen
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('sport')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Sporttauglichkeit
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('fuehrerschein')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Führerschein
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('aesthetik')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Ästhetik
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Termin buchen
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* 2G.2 Feature Grid mit klickbaren Karten */}
      <section id="leistungen" className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-[#032925]">
              Unsere individuellen Leistungen
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spezialFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#8ae9bb]/50 rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all"
                onClick={() => scrollToSection(feature.id)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#032925] mb-2">{feature.title}</h3>
                  <p className="text-[#032925]/70 text-sm mb-4">{feature.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#032925] hover:text-[#1a1308]">
                    Mehr erfahren
                    <ArrowDown className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 2G.2.1 Sporttauglichkeitsuntersuchung */}
      <div id="sport">
        <ContentSection
          title="Sporttauglichkeitsuntersuchung"
          content="Vor Aufnahme oder Wiederaufnahme sportlicher Aktivität prüfen wir Herz-Kreislauf-System, Lungenfunktion und allgemeine Belastbarkeit. Je nach Sportart und persönlichem Risiko ergänzen wir EKG, Belastungstest oder weitere Untersuchungen. So erhalten Sie eine fundierte Einschätzung Ihrer körperlichen Leistungsfähigkeit und trainieren sicher."
          image="/images/Content_Behandlung_4.webp"
          backgroundColor="white"
        />
      </div>

      {/* 2G.2.2 Führerschein- & Eignungsuntersuchungen */}
      <div id="fuehrerschein">
        <ContentSection
          title="Führerschein- & Eignungsuntersuchungen"
          content="Wir führen ärztliche Untersuchungen für Führerschein, berufliche Eignung und besondere Anforderungen durch, zum Beispiel Seh-, Belastungs- oder Reaktionstests. Nach Abschluss erhalten Sie die erforderlichen Bescheinigungen direkt aus unserer Praxis – zuverlässig und zeitnah."
          image="/images/Sehtest_Fuehrerschein.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* 2G.2.3 Reisemedizin & Atteste */}
      <div id="reise">
        <ContentSection
          title="Reisemedizin & Atteste"
          content="Wir beraten Sie vor Reisen zu Impfungen, Infektionsrisiken und individueller Vorsorge je nach Reiseziel. Benötigte Atteste, ärztliche Bescheinigungen oder Reisetauglichkeitsbestätigungen stellen wir nach persönlicher Untersuchung und Beratung aus."
          image="/images/Reisemedizin_Atteste.webp"
          backgroundColor="white"
        />
      </div>

      {/* 2G.2.4 Individuelle Check-up-Programme */}
      <div id="checkup">
        <ContentSection
          title="Individuelle Check-up-Programme"
          content="Unsere Check-up-Programme kombinieren Vorsorge, Laboruntersuchungen, Herz- und Stoffwechselchecks zu einem auf Sie abgestimmten Gesundheitsprofil. Sie erhalten einen klaren Überblick über Ihren aktuellen Gesundheitszustand und konkrete Empfehlungen für Prävention und Lebensstil."
          image="/images/Checkup35_neu.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* 2G.2.5 Ästhetische Behandlungen */}
      <div id="aesthetik">
        <ContentSection
          title="Ästhetische Behandlungen"
          content="Ausgewählte ästhetische Behandlungen wie Faltenbehandlung oder medizinische Hauttherapie erfolgen fachärztlich begleitet und mit höchsten Sicherheitsstandards. Unser Ziel sind natürliche Ergebnisse, eine ehrliche Beratung und ein spürbares Plus an Wohlbefinden."
          image="/images/Botox.webp"
          backgroundColor="white"
        />
      </div>

      {/* 2G.3 Medizinische Leistungen nach Maß */}
      <ContentSection
        title="Medizinische Leistungen nach Maß"
        content="Neben den Leistungen der gesetzlichen Krankenversicherung bieten wir individuelle Untersuchungen und Gesundheitsprogramme an, die gezielt auf Ihre persönlichen Bedürfnisse abgestimmt sind. Dazu gehören spezielle Vorsorgeuntersuchungen, erweiterte Diagnostik und maßgeschneiderte Check-up-Programme für unterschiedliche Lebenssituationen. So erhalten Sie eine medizinische Betreuung, die sich nicht an Standards, sondern an Ihnen orientiert."
        image="/images/4_Termin_Beratung.webp"
        imagePosition="left"
        backgroundColor="gray"
      />

      {/* 2G.4 Kombination aus Vorsorge, Diagnostik und Beratung */}
      <section className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-[#032925]">
                Kombination aus Vorsorge, Diagnostik und Beratung
              </h2>
              <p className="text-[#032925]/80 leading-relaxed mb-6">
                Viele Spezial-Checks lassen sich sinnvoll mit moderner Diagnostik und ärztlicher Beratung verbinden – für fundierte Entscheidungen zu Ihrer Gesundheit. Durch die enge Zusammenarbeit im Medicum Diez können Untersuchungen, Auswertungen und persönliche Gespräche zeitnah kombiniert werden. So entsteht ein ganzheitliches Bild Ihrer gesundheitlichen Situation.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/leistungen/vorsorge" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Vorsorge & Prävention
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/leistungen/diagnostik" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Diagnostik-Zentrum
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/MRT 5.webp"
                  alt="Diagnostik"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2G.6 SEO Content */}
      <SEOContent
        title="Individuelle Gesundheitsleistungen in Diez und Limburg"
        content="Im Medicum Diez bieten wir ein breites Spektrum individueller Gesundheitsleistungen an, darunter Sporttauglichkeitsuntersuchungen, Führerschein- und Eignungsuntersuchungen, Reisemedizin, individuelle Check-ups sowie ausgewählte ästhetische Behandlungen. Unsere Hausärzte beraten Sie persönlich und erstellen auf Wunsch ein auf Ihre Lebenssituation abgestimmtes Vorsorge- und Diagnostikkonzept. Patientinnen und Patienten aus Diez, Limburg und dem gesamten Rhein-Lahn-Kreis profitieren von moderner Medizintechnik, kurzen Wegen und einer vertrauensvollen ärztlichen Betreuung."
      />

      {/* 2G.7 CTA */}
      <div id="termin">
        <CTASection
          title="Beratung zu individuellen Leistungen vereinbaren"
          subtitle="Vereinbaren Sie Ihren Termin zur Beratung über Spezial-Checks und individuelle Gesundheitsleistungen online oder telefonisch."
          buttonText="Beratung buchen"
        />
      </div>
    </Layout>
  );
};

export default SpezialPage;
